import { AttachMoney, Comment, Edit, ExpandMore } from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Profile, WeeklyReport } from "../types";
import { formatSqlDateOnlyForDisplay } from "../utils/dateHelpers";
import EarningsDetailsDialog from "./EarningsDetailsDialog";
import MessagesManager from "./MessagesManager";

interface WeeklyReportsTableProps {
  weeklyReports: WeeklyReport[];
  onEditReport?: (report: WeeklyReport) => void;
  onApproveReport?: (reportId: string) => void;
  onSubmitReport?: (reportId: string) => void;
  onViewDetails?: (report: WeeklyReport) => void;
  user: any;
  profile: Profile | null;
  reportsWithIncomeSources: Set<string>;
}

const WeeklyReportsTable: React.FC<WeeklyReportsTableProps> = ({
  weeklyReports,
  onEditReport,
  onApproveReport,
  onSubmitReport,
  onViewDetails,
  user,
  profile,
  reportsWithIncomeSources,
}) => {
  const { t, i18n } = useTranslation();
  const [earningsDialogOpen, setEarningsDialogOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState<WeeklyReport | null>(
    null
  );
  const [messagesDialogOpen, setMessagesDialogOpen] = useState(false);
  const [selectedReportForMessages, setSelectedReportForMessages] =
    useState<WeeklyReport | null>(null);
  const [expandedReports, setExpandedReports] = useState<Set<string>>(
    new Set()
  );
  const [submitConfirmDialogOpen, setSubmitConfirmDialogOpen] = useState(false);
  const [reportToSubmit, setReportToSubmit] = useState<string | null>(null);

  // Oldest → newest for mileage gap detection (each row vs prior week)
  const reportsChronologicalAsc = [...weeklyReports].sort((a, b) =>
    String(a.week_start_date).localeCompare(String(b.week_start_date))
  );

  const mileageGaps = new Map<string, number>();
  for (let i = 1; i < reportsChronologicalAsc.length; i++) {
    const currentReport = reportsChronologicalAsc[i];
    const previousReport = reportsChronologicalAsc[i - 1];
    const gap = currentReport.start_mileage - previousReport.end_mileage;
    if (gap !== 0) {
      mileageGaps.set(currentReport.id, gap);
    }
  }

  // Newest first for display
  const sortedReports = [...reportsChronologicalAsc].reverse();

  const handleViewEarnings = (report: WeeklyReport) => {
    if (onViewDetails) {
      onViewDetails(report);
    } else {
      setSelectedReport(report);
      setEarningsDialogOpen(true);
    }
  };

  const handleViewMessages = (report: WeeklyReport) => {
    setSelectedReportForMessages(report);
    setMessagesDialogOpen(true);
  };

  const handleCloseEarningsDialog = () => {
    setEarningsDialogOpen(false);
    setSelectedReport(null);
  };

  const toggleReportExpansion = (reportId: string) => {
    setExpandedReports((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(reportId)) {
        newSet.delete(reportId);
      } else {
        newSet.add(reportId);
      }
      return newSet;
    });
  };

  const getReportStatusColor = (status: string) => {
    switch (status) {
      case "draft":
        return "default";
      case "submitted":
        return "warning";
      case "approved":
        return "success";
      case "rejected":
        return "error";
      default:
        return "default";
    }
  };

  // Mobile Card Component
  const MobileReportCard: React.FC<{
    report: WeeklyReport;
    mileageGap?: number;
  }> = ({ report, mileageGap }) => {
    const rideShareIncome = (report as any).ride_share_income || 0;
    const rentalIncome = (report as any).rental_income || 0;
    const taxiIncome = (report as any).taxi_income || 0;
    const totalIncome = rideShareIncome + rentalIncome + taxiIncome;
    const maintenanceExpenses = report.maintenance_expenses || 0;
    const gasExpense = report.gas_expense || 0;
    const totalExpenses = maintenanceExpenses + gasExpense;
    const totalMileage = report.end_mileage - report.start_mileage;
    const isExpanded = expandedReports.has(report.id);

    return (
      <Card
        sx={{
          mb: 2,
          boxShadow:
            mileageGap !== undefined && mileageGap !== 0
              ? "0 2px 12px rgba(244, 67, 54, 0.2)"
              : "0 2px 8px rgba(0,0,0,0.1)",
          border:
            mileageGap !== undefined && mileageGap !== 0
              ? "2px solid rgba(244, 67, 54, 0.5)"
              : "1px solid #e2e8f0",
          borderRadius: 2,
          backgroundColor:
            mileageGap !== undefined && mileageGap !== 0
              ? "rgba(244, 67, 54, 0.02)"
              : "background.paper",
        }}
      >
        {/* Header - Always Visible */}
        <CardContent sx={{ pb: 1 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              cursor: "pointer",
            }}
            onClick={() => toggleReportExpansion(report.id)}
          >
            <Box sx={{ flex: 1 }}>
              {/* Week Period */}
              <Typography variant="h6" fontWeight={600} gutterBottom>
                {formatSqlDateOnlyForDisplay(
                  report.week_start_date,
                  i18n.language
                )}{" "}
                -{" "}
                {formatSqlDateOnlyForDisplay(
                  report.week_end_date,
                  i18n.language
                )}
              </Typography>

              {/* Status and Summary */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 1,
                  flexWrap: "wrap",
                }}
              >
                <Chip
                  label={report.status}
                  color={getReportStatusColor(report.status) as any}
                  size="small"
                  sx={{
                    fontWeight: 600,
                    textTransform: "capitalize",
                    "& .MuiChip-label": {
                      fontSize: "0.75rem",
                    },
                  }}
                />
                {mileageGap !== undefined && mileageGap !== 0 && (
                  <Chip
                    label="⚠️ Mileage Gap"
                    color="error"
                    size="small"
                    sx={{
                      fontWeight: 600,
                      "& .MuiChip-label": {
                        fontSize: "0.75rem",
                      },
                    }}
                  />
                )}
                <Typography variant="body2" color="text.secondary">
                  {totalMileage.toLocaleString()} km •{" "}
                  {totalIncome.toLocaleString()} {t("common.currency")} income
                </Typography>
              </Box>
            </Box>

            {/* Expand/Collapse Icon */}
            <IconButton
              size="small"
              sx={{
                ml: 1,
                transition: "transform 0.2s ease",
                transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
              }}
            >
              <ExpandMore />
            </IconButton>
          </Box>

          {/* Draft Reminder for Drivers and Owners */}
          {report.status === "draft" &&
            (profile?.user_type === "driver" ||
              (profile?.user_type === "owner" &&
                report.driver_id === profile.id)) && (
              <Box
                sx={{
                  mt: 1,
                  p: 1.5,
                  backgroundColor: "rgba(255, 193, 7, 0.1)",
                  border: "1px solid rgba(255, 193, 7, 0.3)",
                  borderRadius: 1,
                  borderLeft: "4px solid #ffc107",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: "warning.dark",
                    fontWeight: 500,
                    fontSize: "0.875rem",
                  }}
                >
                  {t("reports.draftReminder")}
                </Typography>
              </Box>
            )}
        </CardContent>

        {/* Collapsible Details */}
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <CardContent sx={{ pt: 0 }}>
            {/* Mileage */}
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
              >
                {t("reports.mileage")}
              </Typography>
              <Typography variant="body2" color="info.main" fontWeight={600}>
                {report.start_mileage.toLocaleString()} →{" "}
                {report.end_mileage.toLocaleString()}
              </Typography>
              <Typography variant="body2" color="info.main">
                {totalMileage.toLocaleString()} {t("common.km")} total
              </Typography>
            </Box>

            {/* Mileage Gap Warning */}
            {mileageGap !== undefined && mileageGap !== 0 && (
              <Box
                sx={{
                  mb: 2,
                  p: 1.5,
                  backgroundColor: "rgba(244, 67, 54, 0.1)",
                  border: "1px solid rgba(244, 67, 54, 0.3)",
                  borderRadius: 1,
                  borderLeft: "4px solid #f44336",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: "error.main",
                    fontWeight: 600,
                    fontSize: "0.875rem",
                  }}
                >
                  ⚠️ Mileage Gap Detected
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "error.main",
                    fontSize: "0.875rem",
                    mt: 0.5,
                  }}
                >
                  {mileageGap > 0 ? (
                    <>
                      {Math.abs(mileageGap).toLocaleString()} km unaccounted for
                      between this report and the previous one
                    </>
                  ) : (
                    <>
                      {Math.abs(mileageGap).toLocaleString()} km overlap with
                      the previous report
                    </>
                  )}
                </Typography>
              </Box>
            )}

            {/* Incomes */}
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
              >
                {t("reports.incomes")}
              </Typography>
              <Typography variant="body2" color="success.main" fontWeight={600}>
                {totalIncome.toLocaleString()} {t("common.currency")}
              </Typography>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 0.5 }}>
                {rideShareIncome > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    Ride: {rideShareIncome.toLocaleString()}
                  </Typography>
                )}
                {rentalIncome > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    Rental: {rentalIncome.toLocaleString()}
                  </Typography>
                )}
                {taxiIncome > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    Taxi: {taxiIncome.toLocaleString()}
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Expenses */}
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
              >
                {t("reports.expenses")}
              </Typography>
              <Typography variant="body2" color="error.main" fontWeight={600}>
                {totalExpenses.toLocaleString()} {t("common.currency")}
              </Typography>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 0.5 }}>
                {maintenanceExpenses > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    Maint: {maintenanceExpenses.toLocaleString()}
                  </Typography>
                )}
                {gasExpense > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    Gas: {gasExpense.toLocaleString()}
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Driver Earnings */}
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
              >
                {t("reports.driverEarnings")}
              </Typography>
              <Typography variant="body2" color="success.main" fontWeight={600}>
                {report.driver_earnings.toLocaleString()} {t("common.currency")}
              </Typography>
            </Box>
          </CardContent>
        </Collapse>

        <CardActions sx={{ pt: 0, px: 2, pb: 2 }}>
          <Box
            sx={{ display: "flex", gap: 1, flexWrap: "wrap", width: "100%" }}
          >
            {/* Action Buttons */}
            {onEditReport &&
              report.status === "draft" &&
              (profile?.user_type === "driver" ||
                (profile?.user_type === "owner" &&
                  report.driver_id === profile.id)) && (
                <Button
                  size="small"
                  startIcon={<Edit />}
                  onClick={() => onEditReport(report)}
                  sx={{
                    color: "primary.main",
                    borderColor: "primary.main",
                    "&:hover": {
                      bgcolor: "rgba(37, 99, 235, 0.08)",
                    },
                  }}
                >
                  {t("common.edit")}
                </Button>
              )}

            <Button
              size="small"
              startIcon={<AttachMoney />}
              onClick={() => handleViewEarnings(report)}
              sx={{
                color: "success.main",
                borderColor: "success.main",
                "&:hover": {
                  bgcolor: "rgba(5, 150, 105, 0.08)",
                },
              }}
            >
              {t("reports.viewEarnings")}
            </Button>

            <Button
              size="small"
              startIcon={<Comment />}
              onClick={() => handleViewMessages(report)}
              sx={{
                color: "info.main",
                borderColor: "info.main",
                "&:hover": {
                  bgcolor: "rgba(37, 99, 235, 0.08)",
                },
              }}
            >
              {t("reports.viewComments")}
            </Button>

            {/* Status Action Buttons */}
            {user &&
              profile?.user_type === "owner" &&
              report.status === "submitted" &&
              onApproveReport && (
                <Button
                  size="small"
                  variant="contained"
                  color="success"
                  onClick={() => onApproveReport(report.id)}
                  sx={{
                    fontWeight: 500,
                    textTransform: "none",
                    ml: "auto",
                  }}
                >
                  {t("reports.approve")}
                </Button>
              )}

            {user &&
              (profile?.user_type === "driver" ||
                (profile?.user_type === "owner" &&
                  report.driver_id === profile.id)) &&
              report.status === "draft" &&
              onSubmitReport && (
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    setReportToSubmit(report.id);
                    setSubmitConfirmDialogOpen(true);
                  }}
                  disabled={!reportsWithIncomeSources.has(report.id)}
                  title={
                    !reportsWithIncomeSources.has(report.id)
                      ? "Add income sources before submitting"
                      : ""
                  }
                  sx={{
                    fontWeight: 500,
                    textTransform: "none",
                    ml: "auto",
                  }}
                >
                  {t("reports.submit")}
                </Button>
              )}
          </Box>
        </CardActions>
      </Card>
    );
  };

  if (sortedReports.length === 0) {
    return (
      <Box
        sx={{
          textAlign: "center",
          py: 4,
          px: 2,
          backgroundColor: "#f8fafc",
          borderRadius: 2,
          border: "1px solid #e2e8f0",
        }}
      >
        <Typography variant="h6" color="text.secondary" gutterBottom>
          {t("reports.noReportsFound")}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t("reports.noReportsMessage")}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      {/* Card Layout for All Screen Sizes */}
      <Box sx={{ px: { xs: 1, sm: 2 } }}>
        {sortedReports.map((report) => (
          <MobileReportCard
            key={report.id}
            report={report}
            mileageGap={mileageGaps.get(report.id)}
          />
        ))}
      </Box>

      {/* Earnings Details Dialog */}
      <EarningsDetailsDialog
        open={earningsDialogOpen}
        onClose={handleCloseEarningsDialog}
        weeklyReport={selectedReport}
      />

      {/* Messages Manager Dialog */}
      {selectedReportForMessages && profile && (
        <MessagesManager
          weeklyReportId={selectedReportForMessages.id}
          currentUser={profile}
          open={messagesDialogOpen}
          onClose={() => {
            setMessagesDialogOpen(false);
            setSelectedReportForMessages(null);
          }}
        />
      )}

      {/* Submit Confirmation Dialog */}
      <Dialog
        open={submitConfirmDialogOpen}
        onClose={() => setSubmitConfirmDialogOpen(false)}
      >
        <DialogTitle>{t("reports.confirmSubmitReport")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("reports.submitReportConfirmation")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSubmitConfirmDialogOpen(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            onClick={() => {
              if (reportToSubmit && onSubmitReport) {
                onSubmitReport(reportToSubmit);
                setSubmitConfirmDialogOpen(false);
                setReportToSubmit(null);
              }
            }}
            variant="contained"
            color="primary"
          >
            {t("reports.submit")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default WeeklyReportsTable;
